import { RundownToken } from '../../../axios'
import type { RundownPermission } from '@rundown-studio/types'

/**
 * Hook to check rundown permissions using RundownToken as the single source of truth.
 *
 * The backend determines all permissions (team role, event membership, guest access)
 * and encodes them in the RundownToken JWT. This hook simply reads that result.
 *
 * @example
 * ```tsx
 * const { isReady, hasPermission, canEdit } = useRundownAccess()
 *
 * if (!isReady) return <Loading />
 * if (!canEdit) return <ReadOnlyView />
 * ```
 *
 * @returns Object with isReady state and permission check methods
 */
export const useRundownAccess = () => {
  const permissions = RundownToken.permissions

  /**
   * Whether the RundownToken has been initialized with permissions.
   * Components should check this before rendering permission-dependent content.
   */
  const isReady = typeof permissions === 'number'

  /**
   * Check if the current user/guest has a specific permission.
   *
   * @param permission - The RundownPermission flag to check
   * @returns true if the user has the permission, false otherwise
   */
  const hasPermission = (permission: RundownPermission): boolean => {
    if (typeof permissions !== 'number') return false
    return (permissions & permission) !== 0
  }

  return {
    isReady,
    hasPermission,
    /**
     * Raw permission bitmask from the token.
     * Prefer using hasPermission() for checks.
     */
    permissions,
  }
}
